'use client';

import { useOnClickOutside } from 'usehooks-ts';
import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { PRODUCT_QUERY_PARAMS } from '@/features/navigation/constants';
import { useDrawer } from '@/features/drawer/context/use-drawer';
import { IconArrowRight1, IconProgressActivity } from '@/features/icons';
import { IconClose, IconSearchNormal } from '@/components/svg-symbols/icons';
import { QueryKeys } from '@/app/_utils/react-query';
import { useUserToken } from '@/app/_utils/session';
import { cn } from '@/utils/cn';
import { useRouteHash } from '@/utils/use-route-hash';
import { MIN_ACCEPTABLE_QUERY_LENGTH } from './constants';
import Backdrop from './backdrop';
import LastSearchHistory from './last-search-history';
import SearchResultChips from './search-result-chips';
import SearchResultProducts from './search-result-products';
import Suggestions from './suggestions';
import fetchSearchResult from './utils/fetch-search-results';
import { generateSearchUrl } from './utils/search-helpers';
import useSearchHistory from './utils/use-search-history';
import SearchSkeleton from '../components/search-skeleton';
import { MENU_HASH, MENU_SEARCH_HASH, SearchDrawerName } from '../constants';
import { closeMenuOnNavigate } from '../utils/close-menu-on-navigate';
interface EmbeddedInDrawerProps {
  drawerName: string;
  children: React.ReactNode;
}
interface Props {
  mode: 'mobile' | 'desktop';
  embeddedInDrawer?: EmbeddedInDrawerProps;
}
const SearchBox = forwardRef<HTMLInputElement, Props>(function SearchBox({
  mode = 'desktop',
  embeddedInDrawer
}, inputRef) {
  const formRef = useRef(null);
  const {
    closeDrawer,
    isDrawerOpen,
    openDrawer
  } = useDrawer();
  const [ut] = useUserToken();
  const searchParams = useSearchParams();
  const queryParam = searchParams.get(PRODUCT_QUERY_PARAMS.SEARCH);
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebounce(query, 300);
  const [historyItems, addHistoryItem] = useSearchHistory();
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const isMobile = mode === 'mobile';
  const isEmbedded = !!embeddedInDrawer;
  const wasFocusRef = useRef(false);
  const isFocusRef = useRef(isFocus);
  isFocusRef.current = isFocus;
  const isHashUpdateRef = useRef(false);
  const skipAddHashRef = useRef(false);
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const setInputRef = useCallback((el: HTMLInputElement | null) => {
    inputElementRef.current = el;
    if (typeof inputRef === 'function') {
      inputRef(el);
    } else if (inputRef != null && typeof inputRef === 'object' && 'current' in inputRef) {
      inputRef.current = el;
    }
  }, [inputRef]);
  const pathname = usePathname();
  const router = useRouter();
  const {
    addHash,
    removeHash
  } = useRouteHash({
    hash: 'search',
    onHashRemoved: () => {
      if (!isEmbedded) {
        setIsFocus(false);
        inputElementRef.current?.blur();
        if (isMobile) {
          closeDrawer(SearchDrawerName);
        }
      }
    }
  });
  const isMenuOpen = embeddedInDrawer ? isDrawerOpen(embeddedInDrawer.drawerName) : false;
  const isSearchDrawerOpen = !isEmbedded && isDrawerOpen(SearchDrawerName);
  const handleNavigateWithCleanup = (e?: React.MouseEvent, href?: string) => {
    if (!e || !href) {
      closeMenuOnNavigate();
      return;
    }
    const hash = window.location.hash;
    if (hash) {
      e.preventDefault();
      closeMenuOnNavigate(undefined, undefined, true);
      const steps = hash === MENU_SEARCH_HASH ? 2 : 1;
      const handler = () => {
        window.removeEventListener('popstate', handler);
        router.push(href);
      };
      window.addEventListener('popstate', handler);
      history.go(-steps);
    } else {
      closeMenuOnNavigate();
    }
  };
  const handleInputFocus = () => {
    try {
      // @ts-expect-error: Goftino doesn't include types
      Goftino.close();
    } catch (error) {
      // nothing
    }
    return setIsFocus(true);
  };
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname !== pathname) {
      skipAddHashRef.current = true;
      return;
    }
    if (window.location.hash.includes('search')) {
      setIsFocus(true);
      if (isMobile && !isEmbedded) {
        openDrawer(SearchDrawerName);
      }
    } else {
      setIsFocus(false);
      if (isEmbedded) inputElementRef.current?.blur();
      skipAddHashRef.current = true;
    }
  }, [pathname, isEmbedded, isMobile, openDrawer]);
  useEffect(() => {
    if (!isEmbedded && isMobile && !isSearchDrawerOpen) {
      setIsFocus(false);
    }
  }, [isEmbedded, isMobile, isSearchDrawerOpen]);
  useOnClickOutside(formRef, () => {
    if (isMobile && !isEmbedded) return;
    setIsFocus(false);
    if (isEmbedded) inputElementRef.current?.blur();
  });
  const {
    isLoading,
    isError,
    data: response,
    isFetching
  } = useQuery({
    queryKey: QueryKeys.Layout.Search(ut, debouncedQuery),
    queryFn: ({
      signal
    }) => fetchSearchResult({
      ut,
      query: debouncedQuery,
      signal
    }),
    enabled: (debouncedQuery.length === 0 || debouncedQuery.length > MIN_ACCEPTABLE_QUERY_LENGTH) && !!ut,
    placeholderData: keepPreviousData
  });
  const results = response && response.isSuccess ? response.data : null;
  const noResult = !results?.brands?.length && !results?.categories?.length && !results?.products?.length && !(isFetching && isLoading);
  const queryId = results?.queryId;
  const handleSubmit = (e: React.FormEvent) => {
    if (!query.length) {
      e.preventDefault();
      if (mode === 'mobile') return;
      setIsFocus(false);
      inputElementRef.current?.blur();
      return false;
    }
    const trimmedQuery = query.trim();
    setQuery(trimmedQuery);
    addHistoryItem(trimmedQuery);
    if (pathname === '/search' && typeof window !== 'undefined' && window.location.hash.includes('search')) {
      e.preventDefault();
      router.replace(generateSearchUrl(trimmedQuery));
      return false;
    }
    return true;
  };
  const handleSearchResultClick = useCallback((e: React.MouseEvent) => {
    const link = (e.target as HTMLElement).closest('a[href^="/"]');
    if (link && typeof window !== 'undefined' && window.location.hash) {
      e.preventDefault();
      const href = link.getAttribute('href') ?? '/';
      router.replace(href);
    }
  }, [router]);
  useEffect(() => {
    if (!isEmbedded) setIsFocus(isMobile);
  }, [mode, setIsFocus, isMobile, isEmbedded]);
  useEffect(() => {
    if (isEmbedded) {
      setQuery('');
    } else {
      setQuery(queryParam || '');
    }
    if (typeof window === 'undefined' || !window.location.hash.includes('search')) {
      setIsFocus(false);
      if (isEmbedded) inputElementRef.current?.blur();
    }
  }, [queryParam, isEmbedded]);
  useEffect(() => {
    if (!isEmbedded || !isMenuOpen) {
      wasFocusRef.current = isFocus;
      return;
    }
    if (isHashUpdateRef.current) {
      isHashUpdateRef.current = false;
      wasFocusRef.current = isFocus;
      return;
    }
    const search = searchParams.toString();
    const pathWithSearch = search ? `${pathname}?${search}` : pathname;
    if (isFocus && !wasFocusRef.current) {
      const currentHash = window.location.hash;
      if (currentHash !== MENU_SEARCH_HASH) {
        window.history.pushState({}, '', `${pathWithSearch}${MENU_SEARCH_HASH}`);
      }
    } else if (!isFocus && wasFocusRef.current && isMenuOpen) {
      const currentHash = window.location.hash;
      if (currentHash === MENU_SEARCH_HASH) {
        window.history.replaceState({}, '', `${pathWithSearch}${MENU_HASH}`);
      }
    }
    wasFocusRef.current = isFocus;
  }, [isFocus, isEmbedded, isMenuOpen, pathname, searchParams]);
  useEffect(() => {
    if (isEmbedded) return;
    if (typeof window === 'undefined') return;
    if (skipAddHashRef.current) {
      skipAddHashRef.current = false;
      if (!isFocus && window.location.hash.includes('search')) {
        if (pathname === '/search') {
          history.back();
        } else {
          removeHash();
        }
      }
      return;
    }
    if (isFocus) {
      if (!window.location.hash.includes('search')) {
        if (pathname === '/search') {
          const url = `${pathname}${window.location.search}#search`;
          window.history.pushState({}, '', url);
        } else {
          addHash();
        }
      }
    } else if (window.location.hash.includes('search')) {
      if (pathname === '/search') {
        history.back();
      } else {
        removeHash();
      }
    }
  }, [isFocus, isEmbedded, addHash, removeHash, pathname]);
  useEffect(() => {
    if (isEmbedded) return;
    const handlePopState = () => {
      const hash = window.location.hash;
      const hasSearchHash = hash.includes('search');
      const currentlyFocused = isFocusRef.current;
      if (hasSearchHash === currentlyFocused) return;
      isHashUpdateRef.current = true;
      if (hasSearchHash) {
        setIsFocus(true);
        if (isMobile) {
          openDrawer(SearchDrawerName);
        }
      } else {
        setIsFocus(false);
        inputElementRef.current?.blur();
        if (isMobile) {
          closeDrawer(SearchDrawerName);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isEmbedded, isMobile, closeDrawer, openDrawer]);
  useEffect(() => {
    if (!isEmbedded) return;
    const handlePopState = () => {
      const hash = window.location.hash;
      if (hash === MENU_SEARCH_HASH) {
        if (!isFocus) {
          isHashUpdateRef.current = true;
          setIsFocus(true);
        }
      } else if (hash === MENU_HASH) {
        if (isFocus) {
          isHashUpdateRef.current = true;
          setIsFocus(false);
          inputElementRef.current?.blur();
        }
      } else if (!hash || hash === '') {
        if (isFocus) {
          isHashUpdateRef.current = true;
          setIsFocus(false);
          inputElementRef.current?.blur();
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isFocus, isEmbedded]);
  const searchResultsContent = <>
            {isFetching && query.length ? <SearchSkeleton className='mt-2' /> : <div>
                    <span data-test='search-result--empty' className={cn('block text-body-14 text-text-mediumGray', isEmbedded ? 'mb-4' : 'mb-18 lg:mb-7 lg:px-1 lg:text-body-16', query.length > MIN_ACCEPTABLE_QUERY_LENGTH && noResult && !isError ? 'block' : 'hidden')}>
                        برای جست‌وجوی شما نتیجه‌ای پیدا نشد!
                    </span>
                    {isError && query.length && <span data-test='search-result--error' className={cn('mb-2 block text-body-14', isEmbedded ? '' : 'mr-6 lg:px-4 lg:text-body-16')}>
                            جست‌وجو با خطا مواجه شد.
                        </span>}
                    {!isError && <>
                            {results?.categories && <SearchResultChips dataTest='categories' items={results.categories} title='دسته‌بندی‌ها' onNavigate={isEmbedded ? handleNavigateWithCleanup : undefined} />}
                            {results?.brands && <SearchResultChips dataTest='brands' items={results.brands} title='برندها' onNavigate={isEmbedded ? handleNavigateWithCleanup : undefined} />}
                            {!query.length && !results?.queryId && historyItems && <LastSearchHistory items={historyItems} title='آخرین جست‌وجوهای شما' onNavigate={isEmbedded ? handleNavigateWithCleanup : undefined} />}
                            <Suggestions items={results?.popularSearch || []} title='جست‌وجوهای پرطرفدار' onNavigate={isEmbedded ? handleNavigateWithCleanup : undefined} />
                            <SearchResultProducts query={query} queryId={queryId} items={results?.products || null} title='محصولات' />
                        </>}
                </div>}
        </>;
  const inputRow = isEmbedded ? <div className={cn('flex h-14 w-full shrink-0 items-center gap-2 p-2', !embeddedInDrawer && 'border-b border-border-gray-light')}>
            <button type='button' onClick={() => {
      if (isFocus) {
        setQuery('');
        setIsFocus(false);
        inputElementRef.current?.blur();
      } else if (embeddedInDrawer) {
        closeDrawer(embeddedInDrawer.drawerName);
      }
    }} className='shrink-0 cursor-pointer' aria-label={isFocus ? 'بازگشت به منو' : 'بستن'}>
                <IconArrowRight1 className='h-6 w-6 text-text-black' />
            </button>
            <div className='relative flex flex-1 items-center rounded border border-border-gray-medium bg-white'>
                <div className='absolute right-0 top-0 flex h-full items-center justify-center rounded px-1.5'>
                    <IconSearchNormal className='mr-1 h-6 w-6 text-text-mediumGray' />
                </div>
                <input ref={setInputRef} value={query} type='text' autoComplete='off' onFocus={handleInputFocus} placeholder='جست‌وجو در کالاها، دسته‌بندی‌ها و برندها' className='h-10 flex-1 rounded border-none bg-transparent py-2 pl-10 pr-10 text-body-14 outline-none placeholder:text-text-mediumGray' onChange={e => setQuery(e.target.value.trimStart())} name='query' />
                <div className='absolute left-2 flex h-full items-center'>
                    {isFetching && query.length ? <IconProgressActivity className='h-6 w-6 animate-spin text-text-mediumGray' /> : <button type='button' onClick={() => setQuery('')} data-test='clear-result' className={cn('flex text-text-mediumGray', {
          invisible: !query.length
        })}>
                            <IconClose className='h-6 w-6' />
                        </button>}
                </div>
            </div>
        </div> : <div className='relative mb-5 flex h-14 w-full items-center justify-center overflow-hidden rounded lg:mb-0 lg:h-auto'>
            <div className='absolute right-0 top-0 flex h-full items-center justify-center rounded px-1.5'>
                <IconSearchNormal className='mr-8 h-6 w-6 text-text-mediumGray lg:mr-1' />
            </div>
            <div className='absolute left-0 top-0 flex h-full items-center justify-center rounded px-1.5'>
                {isFetching && query.length ? <IconProgressActivity className='mr-1 h-6 w-6 animate-spin text-text-mediumGray' /> : <button type='button' onClick={() => setQuery('')} data-test='clear-result' className={cn('flex text-text-mediumGray', {
        hidden: !query.length
      })}>
                        <IconClose className='mr-1 h-6 w-6' />
                    </button>}
            </div>
            {isMobile && <label onClick={() => {
      if (pathname === '/search') {
        history.back();
      } else {
        removeHash();
        closeDrawer(SearchDrawerName);
      }
    }} className='relative z-10 ml-1 cursor-pointer'>
                    <IconArrowRight1 className='h-6 w-6 text-text-black' />
                </label>}
            <input ref={setInputRef} value={query} type='text' autoComplete='off' onFocus={handleInputFocus} placeholder='جست‌وجو در کالاها، دسته‌بندی‌ها و برندها' className='h-11 flex-1 rounded border border-border-gray-medium py-2.5 pl-2 pr-10 text-body-14 leading-relaxed outline-none placeholder:text-text-mediumGray focus:border-pink-200 md:min-w-[520px] lg:text-body-16' onChange={e => setQuery(e.target.value.trimStart())} name='query' />
        </div>;
  return <>
            <div ref={formRef} className={cn(isEmbedded && 'flex h-full min-h-0 flex-col')}>
                <form className={cn('relative z-top-of-chatbot-plus3 flex flex-wrap rounded border-none', isEmbedded ? 'min-h-0 w-full flex-1 flex-col' : 'mx-auto w-full max-w-xl grow lg:flex', !isEmbedded && (isMobile ? 'flex' : 'hidden'))} autoComplete='off' data-test={isEmbedded ? 'menu-search-form' : 'search-form'} onSubmit={handleSubmit} action={generateSearchUrl()} onClick={handleSearchResultClick}>
                    {inputRow}

                    {isEmbedded ? <div className={cn('flex min-h-0 flex-1 flex-col', isFocus ? 'overflow-auto' : 'overflow-hidden')}>
                            {isFocus ? <div className='hide-scrollbar overflow-auto bg-white px-2 pb-6 pt-2'>
                                    {searchResultsContent}
                                </div> : embeddedInDrawer?.children}
                        </div> : <div data-test='search-result' className={cn('z-10 w-full rounded lg:absolute lg:right-0 lg:top-full lg:mr-0 lg:mt-1 lg:overflow-y-auto lg:border lg:border-border-gray-medium lg:bg-white lg:px-2 lg:py-3 lg:shadow-md', isFocus ? 'flex' : 'hidden')}>
                            {isFocus && <div className='relative w-full leading-4 lg:mt-0'>{searchResultsContent}</div>}
                        </div>}
                </form>
            </div>
            {!isEmbedded && isFocus && <Backdrop />}
        </>;
});
export default SearchBox;